
import { useQuery } from "@tanstack/react-query";
import { getCourse, getCourseLessons } from "@/lib/courses";
import { toast } from "@/components/ui/use-toast";

/**
 * Hook to fetch course details and lessons
 */
export function useCourseDetails(courseId: string | undefined) {
  // Fetch course details
  const {
    data: course,
    isLoading: isCourseLoading,
    error: courseError
  } = useQuery({
    queryKey: ['course', courseId],
    queryFn: async () => {
      try {
        if (!courseId) throw new Error('No course ID provided');
        const data = await getCourse(courseId);
        if (!data) throw new Error('Course not found');
        return data;
      } catch (error) {
        console.error('Error fetching course details:', error);
        const errorMessage = error instanceof Error 
          ? error.message 
          : 'Failed to load course details';
        
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
        
        throw error;
      }
    },
    enabled: !!courseId,
    retry: 1,
    retryDelay: 1000
  });

  // Fetch course lessons
  const {
    data: lessons = [],
    isLoading: isLessonsLoading,
    error: lessonsError
  } = useQuery({
    queryKey: ['course-lessons', courseId],
    queryFn: async () => {
      try {
        if (!courseId) throw new Error('No course ID provided');
        const data = await getCourseLessons(courseId);
        console.log("Fetched lessons:", data);
        if (!data || data.length === 0) {
          toast({
            title: "Notice",
            description: "This course doesn't have any lessons yet."
          });
        }
        return data;
      } catch (error) {
        console.error('Error fetching course lessons:', error);
        const errorMessage = error instanceof Error 
          ? error.message 
          : 'Failed to load course lessons';
        
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
        
        throw error;
      }
    },
    enabled: !!courseId,
    retry: 1,
    retryDelay: 1000
  });

  return {
    course,
    lessons,
    isCourseLoading,
    isLessonsLoading,
    courseError,
    lessonsError
  };
}
